/**
 * WHICH ITEMS THE STOCK PAGE'S OVERVIEW STREAM SHOWS, computable on the server.
 *
 * WHY (2026-10-23, register mobile-first-paint-1021, owner chose "load only the extras"). The stock hub's
 * hidden Alerts and Community tabs repeated, in the HTML, items the visible Overview stream already shows
 * (1,022 of NVDA's 3,564 elements). Their HTML now carries ONLY the items Overview does not show, so a
 * crawler still reads every alert and post exactly once, and the live tape / feed mount when the tab opens.
 * That only works if the server picks EXACTLY the items StockActivityStream renders, so both use this
 * module: the same timestamp rule, the same newest-first order, the same cap.
 *
 * Pure (no 'use client', no React): the page (server) and StockActivityStream (client) both import it.
 */

/** The Overview stream's cap (StockActivityStream's default `limit`). */
export const OVERVIEW_LIMIT = 40

/** Milliseconds for an item's timestamp; 0 when missing or unparseable (such items are not shown). */
export function activityTimeMs(s?: string | number | Date | null): number {
  if (s == null) return 0
  if (s instanceof Date) { const t = s.getTime(); return Number.isFinite(t) ? t : 0 }
  if (typeof s === 'number') return Number.isFinite(s) ? s : 0
  const str = String(s)
  const hasTz = /[zZ]|[+-]\d\d:?\d\d$/.test(str)
  const ms = new Date(hasTz ? str : str.replace(' ', 'T') + 'Z').getTime()
  return Number.isFinite(ms) ? ms : 0
}

export type ActivityItem<N, A, P> =
  | { kind: 'news'; t: number; a: N }
  | { kind: 'alert'; t: number; a: A }
  | { kind: 'post'; t: number; a: P }

/** news + alerts + posts as ONE newest-first timeline, dropping items with no usable time. */
export function mergeActivity<N extends { publishedUtc?: string | null }, A extends { createdAt?: string | null }, P extends { createdAt?: string | null }>(
  news: readonly N[], alerts: readonly A[], posts: readonly P[],
): ActivityItem<N, A, P>[] {
  return [
    ...news.map((a) => ({ kind: 'news' as const, t: activityTimeMs(a.publishedUtc), a })),
    ...alerts.map((a) => ({ kind: 'alert' as const, t: activityTimeMs(a.createdAt), a })),
    ...posts.map((a) => ({ kind: 'post' as const, t: activityTimeMs(a.createdAt), a })),
  ].filter((i) => i.t > 0).sort((x, y) => y.t - x.t)
}

/**
 * The alerts and posts the Overview does NOT show in its first view (filter "All", first `limit` items),
 * by identity, in their original order. These are what the hidden tabs must still carry in the HTML.
 */
export function itemsNotInOverview<N extends { publishedUtc?: string | null }, A extends { createdAt?: string | null }, P extends { createdAt?: string | null }>(
  news: readonly N[], alerts: readonly A[], posts: readonly P[], limit = OVERVIEW_LIMIT,
): { alerts: A[]; posts: P[] } {
  const shown = new Set<unknown>(mergeActivity(news, alerts, posts).slice(0, limit).map((i) => i.a))
  return { alerts: alerts.filter((a) => !shown.has(a)), posts: posts.filter((p) => !shown.has(p)) }
}
